import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { changeImage, changeTitle } from "../actions/logo";
import { getClientTheme } from "../services/public.service";
import { getRGB } from "../components/cssToFilter/cssToFilter";
import config from "../config";
import { isNullOrUndefined } from "../utils";

const useClientWebsite = (
	clientName: string,
	setLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
	const dispatch = useDispatch();

	const changeFavicon = (url: any) => {
		let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
		if (!link) {
			link = document.createElement("link");
			link.rel = "icon";
			link.href = url;
			document.head.appendChild(link);
		} else {
			link.href = url;
		}
	};

	//Function to add list of meta data get from api
	const handleWebsiteMeta = (list: any) => {
		const metaList = [
			"description",
			"keywords",
			"author",
			"og:title",
			"og:description",
		];
		const themeMetaList = JSON.parse(list);

		let count = 0;

		themeMetaList.forEach((data: any) => {
			if (data !== "") {
				const meta = document.createElement("meta");
				if (count < 3) {
					meta.name = metaList[count];
				} else {
					meta.setAttribute("property", metaList[count]);
				}
				meta.content = data;
				document.head.appendChild(meta);
			}
			count++;
		});
	};

	const hexToRgb = (hex: string): string => {
		// Remove the leading '#' if present
		const normalizedHex = hex.startsWith("#") ? hex.slice(1) : hex;

		// Parse the red, green, and blue components
		const bigint = parseInt(normalizedHex, 16);
		const r = (bigint >> 16) & 255;
		const g = (bigint >> 8) & 255;
		const b = bigint & 255;

		// Return as an RGB string
		return `${r},${g},${b}`;
	};

	const updateAllCSSVariables = (
		color: string,
		color2: string
	): Promise<void> => {
		return new Promise((resolve, reject) => {
			const variableList = [
				"--sidebar",
				"--form-control",
				"--color2",
				"--color3",
				"--color4",
			];

			// Get the root element (:root)
			const root = document.documentElement;

			try {
				for (const variable of variableList) {
					// Check if the variable exists in computed styles
					if (getComputedStyle(root).getPropertyValue(variable)) {
						if (variable === "--form-control") {
							root.style.setProperty(variable, `rgba(${hexToRgb(color)}, 0.3)`);
						}
						else {
							root.style.setProperty(variable, `${color}`);
						}
					}
				}

				resolve();
			} catch (error) {
				console.log(error);
				reject(error);
			}
		});
	};

	// Function to fetch website data (style , image,  website title and meta data)
	const getWebsiteData = async (clientName: string) => {
		try {
			const result = await getClientTheme();
			const themedata = result.data;

			document.title = themedata.client_title || "";
			dispatch(changeTitle(themedata.client_title));
			if (!isNullOrUndefined(themedata.client_logo_url)) {
				dispatch(
					changeImage(`${config.awsUrl}${config.region}/Landing/brand.png`)
				);
			}
			changeFavicon(
				`${config.awsUrl}${config.region}/Landing/` + themedata.client_logo_url
			);
			handleWebsiteMeta(themedata.client_meta);
			if (!isNullOrUndefined(themedata.theme_primary_color)) {
				updateAllCSSVariables(
					`#${themedata.theme_primary_color}`,
					`#${themedata.theme_secondary_color}`
				).then(() => {
					setLoading(false);
				});
			} else {
				setLoading(false);
			}
		} catch (error) {
			console.log("Error fetching website data:", error);
			setLoading(false);
		}
	};

	// Use effect to fetch data on component mount
	useEffect(() => {
		getWebsiteData(clientName);
	}, [clientName]);
};

export default useClientWebsite;
